import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import Sidebar from './components/Sidebar';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Attendance from './pages/Attendance';
import FaceAttendance from './pages/FaceAttendance';
import LiveTracking from './pages/LiveTracking';
import AttendanceRules from './pages/AttendanceRules';
import Departments from './pages/Departments';
import Holidays from './pages/Holidays';
import Leaves from './pages/Leaves';
import Locations from './pages/Locations';
import Readers from './pages/Readers';
import Cameras from './pages/Cameras';
import EdgeAgents from './pages/EdgeAgents';
import CameraAnalytics from './pages/CameraAnalytics';
import Roles from './pages/Roles';
import Sections from './pages/Classes';
import Subjects from './pages/Subjects';
import Timetable from './pages/Timetable';
import Users from './pages/Users';
import Reports from './pages/Reports';
import ParentPortal from './pages/ParentPortal';
import StudentAttendance from './pages/StudentAttendance';
import Entities from './pages/Entities';

const Layout = ({ children }) => (
  <div className="app-shell flex min-h-screen">
    <Sidebar />
    <main className="app-main-panel ml-72 min-w-0 flex-1 p-6 lg:p-8">
      <div className="mx-auto min-w-0 max-w-[1480px]">{children}</div>
    </main>
  </div>
);

const DefaultRoute = () => {
  const { user } = useAuth();

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (Array.isArray(user.permissions) && user.permissions.includes('parent.view')) {
    return <Navigate to="/my-children" replace />;
  }

  if (user.role === 'student') {
    return <Navigate to="/my-attendance" replace />;
  }

  return <Navigate to="/dashboard" replace />;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <Toaster
          position="top-right"
          toastOptions={{
            style: {
              borderRadius: '16px',
              border: '1px solid rgba(18, 56, 75, 0.08)',
              background: 'rgba(255, 250, 242, 0.96)',
              color: '#1d2733',
              boxShadow: '0 18px 42px rgba(18, 56, 75, 0.12)',
            },
          }}
        />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/entities" element={
            <ProtectedRoute platformOnly>
              <Layout><Entities /></Layout>
            </ProtectedRoute>
          }/>
          <Route path="/dashboard" element={
            <ProtectedRoute permission="dashboard.view" disallowRoles={['student']}>
              <Layout><Dashboard /></Layout>
            </ProtectedRoute>
          }/>
          <Route path="/attendance" element={
            <ProtectedRoute permission="attendance.view" disallowRoles={['student']}>
              <Layout><Attendance /></Layout>
            </ProtectedRoute>
          }/>
          <Route path="/face-attendance" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <Layout><FaceAttendance /></Layout>
            </ProtectedRoute>
          }/>
          <Route path="/live-tracking" element={
            <ProtectedRoute permission="readers.view" disallowRoles={['student']}>
              <Layout><LiveTracking /></Layout>
            </ProtectedRoute>
          }/>
          <Route path="/attendance-rules" element={
            <ProtectedRoute permission="attendance.manage">
              <Layout><AttendanceRules /></Layout>
            </ProtectedRoute>
          }/>
          <Route path="/holidays" element={
            <ProtectedRoute permission="holidays.view">
              <Layout><Holidays /></Layout>
            </ProtectedRoute>
          }/>
          <Route path="/leaves" element={
            <ProtectedRoute permission="leave.view">
              <Layout><Leaves /></Layout>
            </ProtectedRoute>
          }/>
          <Route path="/users" element={
            <ProtectedRoute permission="users.view">
              <Layout><Users /></Layout>
            </ProtectedRoute>
          }/>
          <Route path="/departments" element={
            <ProtectedRoute permission="departments.view">
              <Layout><Departments /></Layout>
            </ProtectedRoute>
          }/>
          <Route path="/roles" element={
            <ProtectedRoute permission="roles.view">
              <Layout><Roles /></Layout>
            </ProtectedRoute>
          }/>
          <Route path="/locations" element={
            <ProtectedRoute permission="locations.view">
              <Layout><Locations /></Layout>
            </ProtectedRoute>
          }/>
          <Route path="/sections" element={
            <ProtectedRoute permission="sections.view">
              <Layout><Sections /></Layout>
            </ProtectedRoute>
          }/>
          <Route path="/subjects" element={
            <ProtectedRoute permission="subjects.view">
              <Layout><Subjects /></Layout>
            </ProtectedRoute>
          }/>
          <Route path="/timetable" element={
            <ProtectedRoute permission="timetable.view">
              <Layout><Timetable /></Layout>
            </ProtectedRoute>
          }/>
          <Route path="/readers" element={
            <ProtectedRoute permission="readers.view">
              <Layout><Readers /></Layout>
            </ProtectedRoute>
          }/>
          <Route path="/cameras" element={
            <ProtectedRoute permission="cameras.view">
              <Layout><Cameras /></Layout>
            </ProtectedRoute>
          }/>
          <Route path="/edge-agents" element={
            <ProtectedRoute permission="cameras.view">
              <Layout><EdgeAgents /></Layout>
            </ProtectedRoute>
          }/>
          <Route path="/camera-analytics" element={
            <ProtectedRoute permission="cameras.view">
              <Layout><CameraAnalytics /></Layout>
            </ProtectedRoute>
          }/>
          <Route path="/reports" element={
            <ProtectedRoute>
              <Layout><Reports /></Layout>
            </ProtectedRoute>
          }/>
          <Route path="/my-children" element={
            <ProtectedRoute permission="parent.view">
              <Layout><ParentPortal /></Layout>
            </ProtectedRoute>
          }/>
          <Route path="/my-attendance" element={
            <ProtectedRoute permission="attendance.view" allowedRoles={['student']}>
              <Layout><StudentAttendance /></Layout>
            </ProtectedRoute>
          }/>
          <Route path="*" element={<DefaultRoute />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
